/* eslint-disable complexity */
/* eslint-disable max-lines-per-function, max-statements, max-lines */
import { useLazyQuery, useMutation } from "@apollo/client";
import usePrevious from "@bluframe/grapple/usePrevious";
import { mutations, queries } from "@bluframe/mightychroma-mightytool-graphql-schema";
import { useDeepCompareMemoize } from "@bluframe/mightychroma-mightytool-ui";
import useEightLeggedFreakList from "hooks/useEightLeggedFreakList";
import usePreviews from "hooks/usePreviews";
import useReviews from "hooks/useReviews";
import useUser from "hooks/useUser";
import { useCallback, useEffect, useMemo } from "react";
const { critics: { ADD_PREVIEW }, eightLeggedFreak: { EIGHT_LEGGED_FREAK_REVIEW_UPDATE } } = mutations;
const { GET_OMDB_ID } = queries;
// Supported image roles for preview images
const ROLES = [
    "back",
    "front",
    "overview",
    "slip",
    "slipback"
];
const ZERO = 0;
const THREE = 3;
/**
 * useHomeVideoReviewCard
 *
 * @description
 * Manages the state and actions for a single Home Video Review card.
 * Handles preview generation, flagging, and selection callbacks.
 *
 * @param {UseHomeVideoReviewCardProps} params
 * @param {string} params.name - Display name for the review service
 * @param {ReviewsServiceName} params.serviceName - Service identifier
 * @param {SetReviewsIMDbId} [params.setIMDbId] - Optional callback to set IMDb ID
 *
 * @returns {Omit<ReviewsCardProps, "actions">} Card display and action props
 *
 */
const useHomeVideoReviewCard = ({ name, serviceName, setIMDbId }) => {
    const { addImage } = usePreviews();
    const { setIsToggleFlaggedLoading, setReview, setSelectedId, setSelectedIsPreviewLoading, setSelectedIsPreviewValid, state } = useReviews();
    const { isAdmin, isEditor } = useUser();
    // Extract the current service's state from the reviews context
    const serviceState = useMemo(() => state[serviceName], [state, serviceName]);
    const previousServiceState = usePrevious(serviceState);
    // Type guard to narrow string keys to valid ImageRole values
    const isRole = useCallback((key) => ROLES.includes(key), []);
    // Mutation to generate a preview from a review URL
    const [addPreview] = useMutation(ADD_PREVIEW, {
        onCompleted: (data) => {
            setSelectedIsPreviewLoading(false, serviceName);
            setSelectedIsPreviewValid(data.addPreview.isValid, serviceName);
            for (const key of Object.keys(data.addPreview.images ?? {})) {
                const image = data.addPreview.images?.[key];
                const role = isRole(key) ? key : undefined;
                if (image && role) {
                    addImage({ originalUrl: image, role });
                }
            }
            const { errorFields, isValid, ...preparedReview } = data.addPreview;
            setReview({
                audio: {
                    quote: preparedReview.audio?.quote ?? undefined,
                    score: preparedReview.audio?.score ?? undefined,
                    section: preparedReview.audio?.section ?? undefined
                },
                author: {
                    name: preparedReview.author?.name ?? undefined,
                    url: preparedReview.author?.url ?? undefined
                },
                editions: preparedReview.editions ?? undefined,
                extra: {
                    quote: preparedReview.extra?.quote ?? undefined,
                    score: preparedReview.extra?.score ?? undefined,
                    section: preparedReview.extra?.section ?? undefined
                },
                images: {
                    back: preparedReview.images?.back ?? undefined,
                    front: preparedReview.images?.front ?? undefined,
                    overview: preparedReview.images?.overview ?? undefined,
                    slip: preparedReview.images?.slip ?? undefined,
                    slipback: preparedReview.images?.slipback ?? undefined
                },
                movie: {
                    quote: preparedReview.movie?.quote ?? undefined,
                    score: preparedReview.movie?.score ?? undefined,
                    section: preparedReview.movie?.section ?? undefined
                },
                releaseDate: preparedReview.releaseDate ?? undefined,
                serviceName,
                techSpecs: preparedReview.techSpecs,
                total: {
                    quote: preparedReview.total?.quote ?? undefined,
                    score: preparedReview.total?.score ?? undefined,
                    section: preparedReview.total?.section ?? undefined
                },
                url: preparedReview.url,
                video: {
                    quote: preparedReview.video?.quote ?? undefined,
                    score: preparedReview.video?.score ?? undefined,
                    section: preparedReview.video?.section ?? undefined
                }
            }, serviceName);
        }
    });
    // Lazy query to fetch the OMDB ID for a review by its ID
    const [getOmdbId] = useLazyQuery(GET_OMDB_ID, {
        onCompleted: (omdbIdData) => {
            if (setIMDbId && omdbIdData?.omdbId) {
                setIMDbId(omdbIdData.omdbId);
            }
        }
    });
    // Refetch function for the EightLeggedFreak review list
    const { refetch } = useEightLeggedFreakList(serviceName);
    // Mutation to toggle the flagged status of a review
    const [eightLeggedFreakReviewUpdate, { loading: flagLoading }] = useMutation(EIGHT_LEGGED_FREAK_REVIEW_UPDATE);
    // Sync the flag loading state from the mutation to the reviews context
    useEffect(() => {
        if (flagLoading !== state.isToggleFlaggedLoading) {
            setIsToggleFlaggedLoading(flagLoading);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flagLoading, state.isToggleFlaggedLoading]);
    // Auto-preview: when a new URL is selected, generate a preview
    useEffect(() => {
        if (serviceState.selected.url &&
            serviceState.selected.url !== previousServiceState?.selected.url &&
            !serviceState.selected.isCreated &&
            !serviceState.selected.isPreviewLoading &&
            !serviceState.selected.isPreviewValid) {
            setSelectedIsPreviewLoading(true, serviceName);
            addPreview({
                variables: {
                    previewAddInput: {
                        isUHD: state.isUHD,
                        serviceName,
                        url: serviceState.selected.url
                    }
                }
            });
        }
    }, [
        addPreview,
        serviceName,
        serviceState.selected.id,
        serviceState.selected.isCreated,
        serviceState.selected.isPreviewLoading,
        serviceState.selected.isPreviewValid,
        serviceState.selected.url,
        setSelectedIsPreviewLoading,
        state.isUHD
    ]);
    // Filter display list based on "Only New" / "Only Present" toggles
    const display = useMemo(() => {
        if (serviceState.isOnlyNew) {
            return serviceState.display.filter(({ isNew }) => isNew);
        }
        if (serviceState.isOnlyPresent) {
            return serviceState.display.filter(({ isNew }) => !isNew);
        }
        return serviceState.display;
    }, [serviceState.display, serviceState.isOnlyNew, serviceState.isOnlyPresent]);
    // Further filter to exclude flagged reviews unless "Show Flagged" is active
    const filteredDisplay = useDeepCompareMemoize(useMemo(() => {
        if (serviceState.isShowFlagged) {
            return display;
        }
        return display.filter(({ isFlagged }) => !isFlagged);
    }, [display, serviceState.isShowFlagged]));
    // Dynamic subheader text based on current filter state
    const subheader = useMemo(() => {
        if (serviceState.isOnlyNew) {
            return `Total New: ${filteredDisplay.length}`;
        }
        if (serviceState.isOnlyPresent) {
            return `Total Present: ${filteredDisplay.length}`;
        }
        return `Total Available: ${filteredDisplay.length}`;
    }, [
        filteredDisplay.length,
        serviceState.isOnlyNew,
        serviceState.isOnlyPresent
    ]);
    // Card title derived from the service name
    const title = useMemo(() => `${name} Reviews`, [name]);
    // Avatar text: first 3 characters of the service name
    const avatar = useMemo(() => name.slice(ZERO, THREE), [name]);
    // Callback to toggle a review's flagged status
    const onToggleFlagged = useCallback((id, isFlagged) => () => {
        eightLeggedFreakReviewUpdate({
            onCompleted: () => {
                refetch({
                    eightLeggedFreakListInput: {
                        isUHD: state.isUHD,
                        serviceName
                    }
                });
            },
            variables: {
                eightLeggedFreakReviewUpdateInput: {
                    id,
                    isFlagged: !isFlagged
                },
                serviceName
            }
        });
    }, [eightLeggedFreakReviewUpdate, refetch, serviceName, state.isUHD]);
    // Callback to select a review and optionally fetch its IMDb ID
    const onSelect = useCallback((value) => {
        setSelectedId(value, serviceName);
        if (setIMDbId) {
            getOmdbId({
                variables: { id: value, serviceName }
            });
        }
    }, [getOmdbId, serviceName, setSelectedId, setIMDbId]);
    return {
        avatar,
        display: filteredDisplay,
        flagLoading,
        isShowFlagButton: isAdmin || isEditor,
        onSelect,
        onToggleFlagged,
        selected: serviceState.selected.id,
        serviceName,
        subheader,
        title
    };
};
export default useHomeVideoReviewCard;
